<template>
  <SimpleSkeleton v-if="loading" class="p-5" :options="skeletonAdmision" />
  <div v-else-if="admisionData !== null">
    <div class="max-w-340 px-4 sm:px-6 lg:px-8 mx-auto my-4 sm:my-10">
      <PatientInfoCard
        v-if="admisionData"
        class="w-full"
        :admision="admisionData"
        :show-historial="false"
        :show-graficas="false"
        :show-technologias="false"
        :show-alerts="false"
        :show-alert-button="false"
        @origen-cargado="actualizarOrigenAtencion"
      />
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div
          class="bg-white col-span-5 p-4 rounded-lg shadow-sm border border-gray-100"
        >
          <div class="flex justify-between">
            <h2 class="text-2xl font-semibold text-gray-500 mb-3 flex items-center">
              Detalles de admisión
            </h2>
            <div class="flex items-center justify-between gap-2">
              <ButtonDefault
                v-if="puedeFirmarConsentimiento"
                title="Consentimientos"
                @click="abrirConsentimiento"
              >
                <DocumentTextIcon class="shrink-0 size-4" />
              </ButtonDefault>
              <ButtonDefault
                v-if="validatePermiso(['editarAdmision'])"
                data-test="editar-admision"
                color="blue"
                title="Editar admisión"
                :is-loading="editAdmi"
                @click="editarAdmision"
              >
                <PencilIcon class="shrink-0 size-4" />
              </ButtonDefault>
            </div>
          </div>
          <p class="text-slate-500 text-sm mt-1">
            Id: #{{ admisionData?.ciadId }}
          </p>
          <div v-if="detalleInasistencia" class="mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
            <p class="text-slate-600 text-sm">
              <span class="font-bold">Motivo de inasistencia:</span>
              {{ detalleInasistencia.motivo }}
            </p>
            <p v-if="detalleInasistencia.descripcion" class="text-slate-500 text-sm mt-1">
              <span class="font-bold">Descripción:</span>
              {{ detalleInasistencia.descripcion }}
            </p>
            <p class="text-slate-500 text-sm mt-1">
              <span class="font-bold">Estado previo:</span>
              {{ huboAdmision ? 'ADMISIONADO' : 'AGENDADO' }}
            </p>
          </div>
          <!-- Trazabilidad: timeline si hay eventos, fallback a campos de auditoría -->
          <template v-if="trazabilidad.length">
            <div class="mt-2 space-y-1">
              <div v-for="evento in trazabilidad" :key="evento.auloId">
                <p class="text-slate-500 text-sm">
                  <span class="font-bold">{{ eventoLabel[evento.auloEvento ?? '']?.label ?? evento.auloEvento }}:</span>
                  {{ evento.createdBy }}
                  <span class="text-slate-400 ml-1">· {{ formatearFechaEnEspanolConHora(evento.createdAt as string) }}</span>
                </p>
                <template v-if="evento.auloEvento === 'reabrir-historia' && (evento.auloMetadata?.cambios as any[])?.length">
                  <p
                    v-for="(cambio, ci) in (evento.auloMetadata?.cambios as any[])"
                    :key="`${evento.auloId}-${ci}`"
                    class="text-slate-400 text-xs ml-3"
                  >
                    └ {{ formatCambio(cambio) }}
                  </p>
                </template>
              </div>
            </div>
          </template>
          <template v-else>
            <p v-if="admisionData?.createdBy" class="text-slate-500 text-sm mt-1">
              <span class="font-bold">Creado por:</span>
              {{ admisionData?.createdBy }}
            </p>
            <p v-if="admisionData?.createdAt" class="text-slate-500 text-sm mt-1">
              <span class="font-bold">Fecha de creación:</span>
              {{ formatearFechaEnEspanolConHora(admisionData.createdAt as string) }}
            </p>
          </template>
          <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-3">
              <div>
                <span class="text-sm font-medium text-gray-500"
                  >Tipo de Admisión</span
                >
                <p class="font-semibold">
                  {{ admisionData?.ciadTipoAdmision }}
                </p>
              </div>
              <div>
                <ColumnStatusTable :items="datosAdmision" />
              </div>
              <p
                v-if="firstTecnology?.cittNumeroAutorizacion"
                class="text-sm text-gray-600"
              >
                <span class="font-bold">Autorización:</span>
                {{ firstTecnology?.cittNumeroAutorizacion }}
              </p>
              <p class="text-sm text-gray-600">
                <span class="font-bold">Sala:</span>
                {{ firstTecnology?.sala?.comaNombrecorto }}
              </p>
              <p
                v-if="firstTecnology?.cittEntidadOrden"
                class="text-sm text-gray-600"
              >
                <span class="font-bold">Entidad que ordenó:</span>
                {{ firstTecnology?.cittEntidadOrden }}
              </p>
              <p
                v-if="firstTecnology?.cittMedOrden"
                class="text-sm text-gray-600"
              >
                <span class="font-bold">Médico que ordenó:</span>
                {{ firstTecnology?.cittMedOrden }}
              </p>
              <p
                v-if="firstTecnology?.cittTipoTurno"
                class="text-sm text-gray-600"
              >
                <span class="font-bold">Turno:</span>
                {{ firstTecnology?.cittTipoTurno }}
              </p>
              <p v-if="firstTecnology?.cittHora" class="text-sm text-gray-600">
                <span class="font-bold">Hora:</span>
                {{ firstTecnology?.cittHora }}
              </p>
              <p v-if="firstTecnology?.cittVale" class="text-sm text-gray-600">
                <span class="font-bold">Vale electronico:</span>
                {{ firstTecnology?.cittVale }}
              </p>
            </div>
            <div class="space-y-3">
              <div v-if="admisionData?.regimen">
                <span class="text-sm font-medium text-gray-500">Régimen</span>
                <p class="font-semibold">
                  {{ admisionData?.regimen?.comaNombrecorto }}
                </p>
              </div>
              <div>
                <span class="text-sm font-medium text-gray-500"
                  >Tipo de recaudo</span
                >
                <p class="font-semibold">
                  {{ firstTecnology?.tiporecaudo?.comaNombrecorto }}
                </p>
              </div>
              <div>
                <span class="text-sm font-medium text-gray-500">Copago</span>
                <p class="font-semibold">
                  $ {{ admisionData?.ciadCopago?.toLocaleString() }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-white col-span-3 p-4 rounded-lg shadow-sm mt-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-semibold text-gray-500 mb-3 flex items-center">
            Servicios y Tecnologías
          </h2>
          <PrintButtonDefault
            v-if="hasMultipleTramitadosLabs && !esOrdenExterna"
            data-test="imprimir-consolidado"
            :admision="admisionData"
            :tipo-impresion="tipoImpresionAsistencialDefault.LABORATORIOS"
            :is-simple-print="true"
            button-text="PDF Consolidado"
          />
        </div>
        <div
          v-for="tecnologia in admisionData?.technologias"
          :key="tecnologia.cittId"
          class="p-4 space-y-3"
        >
          <div class="flex justify-between">
            <div class="flex flex-col gap-2">
              <h3 class="font-semibold">
                {{ tecnologia?.contratoxservicio?.servicio?.coseNombre }}
              </h3>
              <div>
                <p class="text-sm text-gray-600">
                  {{ formatearCupsServicio(tecnologia.contratoxservicio) || tecnologia?.cittTechnologia }}
                </p>
                <span
                  class="inline-flex items-center rounded-md px-2 py-0.5 font-medium text-sm mr-3"
                  :class="getStatusClass(tecnologia.cittEstado)"
                >
                  {{ tecnologia.cittEstado }}
                </span>
                  <ButtonDefault
                    v-if="tecnologia.cittEstado == 'TRAMITADO' && !tecnologia.cittMotivoInasistencia && admisionData && admisionData.ciadTipoAdmision && !esOrdenExterna"
                    data-test="ver-tecnologia"
                    title="Ver tecnología"
                    type-style="outline"
                    size="xs"
                    color="green"
                    class="mr-2"
                    @click="pushToRouteByStateTech('View', admisionData.ciadTipoAdmision, tecnologia.cittId as number, admisionData.ciadId)"
                  >
                    <EyeIcon class="shrink-0 size-4"/>
                  </ButtonDefault>
                  <PrintButtonDefault
                    v-if="tecnologia.cittEstado == 'TRAMITADO' && admisionData?.ciadTipoAdmision !== 'VACUNA' && !esOrdenExterna"
                    :id="tecnologia.cittId"
                    data-test="imprimir-tecnologia"
                    :tipo-impresion="tipoImpresionComputed"
                    :is-preview="true"
                    :mini="true"
                  />
              </div>
              <div>
                <span
                  v-if="tecnologia?.cittFechaInicio"
                  class="inline-flex items-center text-sm"
                >
                  {{ formatearFechaEnEspanol(tecnologia?.cittFechaInicio) }}
                </span>
                <span class="inline-flex items-center text-sm mx-1.5">

                </span>
                <span
                  v-if="tecnologia?.empleado?.copeFullName"
                  class="inline-flex items-center text-sm font-bold"
                >
                  {{ tecnologia?.empleado?.copeFullName }} - {{ tecnologia?.empleado?.cargo?.comaNombrecorto }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-6 min-w-32">
              <div class="text-xs">
                <p class="font-bold">Valor servicio</p>
                <p class="font-semibold">$ {{ tecnologia.cittValor }}</p>
              </div>
              <div class="text-xs">
                <p class="font-bold">Recaudo</p>
                <p class="font-semibold">$ {{ tecnologia.cittCopago }}</p>
              </div>
            </div>
          </div>
          <template v-if="tecnologia.diagnosticos && tecnologia.diagnosticos.length > 0">
            <div class="mx-auto">
              <h4 class="font-semibold">Diagnósticos:</h4>
              <table class="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                <thead class="bg-gray-50 dark:bg-neutral-800">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-start whitespace-nowrap">
                      <span class="text-xs font-semibold uppercase text-gray-800 dark:text-neutral-200">
                        CIE-10
                      </span>
                    </th>
                    <th scope="col" class="px-6 py-3 text-start whitespace-nowrap min-w-64">
                      <span class="text-xs font-semibold uppercase text-gray-800 dark:text-neutral-200">
                        Nombre
                      </span>
                    </th>

                    <th scope="col" class="px-6 py-3 text-start whitespace-nowrap">
                      <span class="text-xs font-semibold uppercase text-gray-800 dark:text-neutral-200">
                        Tipo
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-neutral-700">
                  <tr v-for="diagnostico in tecnologia.diagnosticos" :key="diagnostico.cidiId">
                    <td class="size-px whitespace-nowrap px-6 py-3">
                      <div class="flex items-center gap-x-3">
                        {{ diagnostico.cie10?.comaNombrecorto }}
                      </div>
                    </td>
                    <td class="size-px whitespace-nowrap px-6 py-3">
                      <div class="flex items-center gap-x-3">
                        {{ diagnostico.cie10?.comaNombrelargo }}
                      </div>
                    </td>
                    <td class="size-px whitespace-nowrap px-6 py-3">
                      <div class="flex items-center gap-x-3">
                        {{ diagnostico.clase?.comaNombrelargo }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
          <template v-if="tecnologia.documentos && tecnologia.documentos?.length > 0">
            <div class="mx-auto">
              <h4 class="font-semibold">Documentos firmados durante la atencion:</h4>
              <table class="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                <thead class="bg-gray-50 dark:bg-neutral-800">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-start whitespace-nowrap">
                      <span class="text-xs font-semibold uppercase text-gray-800 dark:text-neutral-200">
                        Documento
                      </span>
                    </th>

                    <th scope="col" class="px-6 py-3 text-start whitespace-nowrap">
                      <span class="text-xs font-semibold uppercase text-gray-800 dark:text-neutral-200">
                        -
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-neutral-700">
                  <tr v-for="documento in tecnologia.documentos" :key="documento.cifoId">
                    <td class="size-px whitespace-nowrap px-6 py-3">
                      <div class="flex items-center gap-x-3">
                        {{ documento.cifoNombre }}
                      </div>
                    </td>
                    <td class="size-px whitespace-nowrap px-6 py-3">
                      <div class="flex items-center gap-x-3">
                        <ButtonDefault
                          type-style="outline"
                          size="xs"
                          color="red"
                          class="mr-2"
                          @click="openAndGeneratePdf(documento)"
                        >
                          PDF
                        </ButtonDefault>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
      <div v-if="admisionData?.documentos && admisionData?.documentos?.length > 0" class="bg-white col-span-3 p-4 rounded-lg shadow-sm mt-6">
        <h2 class="text-2xl font-semibold text-gray-500 mb-3 flex items-center">
          Documentos firmados durante la admisión
        </h2>
        <div
          v-for="documento in admisionData?.documentos"
          :key="documento.cifoId"
          class="p-4 space-y-3"
        >
          <div class="flex justify-between">
            <div class="flex flex-col gap-2">
              <h3 class="font-semibold">
                {{ documento.cifoNombre }}
              </h3>
              <div>
                <ButtonDefault
                    type-style="outline"
                    size="xs"
                    color="red"
                    class="mr-2"
                    @click="openAndGeneratePdf(documento)"
                  >
                    PDF
                  </ButtonDefault>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { formatearCupsServicio } from '@/modules/Contratacion/utils/cupsServicio'
import { computed, onMounted, ref } from 'vue'
import type { ICitaAdmision } from '@/modules/Admision/interfaces'
import type { IAudiEvento } from '@/modules/Core/interfaces/tablas/IAudiEvento'
import { closeModal, formatearFechaEnEspanol, formatearFechaEnEspanolConHora, getStatusClass, validatePermiso } from '@/modules/Core/usings/generales'
import { trazabilidadService } from '@/services'
import ColumnStatusTable from '@/modules/Core/components/general/ColumnStatusTable.vue'
import ButtonDefault from '@/modules/Core/components/general/ButtonDefault.vue'
import { DocumentTextIcon, EyeIcon, PencilIcon } from '@heroicons/vue/24/outline'
import { useRouter } from 'vue-router'
import { admisionService } from '@/services'
import PrintButtonDefault from '@/modules/Asistencial/components/PrintButtonDefault.vue'
import { useAdmisionTechComponents } from '@/modules/Asistencial/composables'
import { tipoImpresionAsistencialDefault } from '@/modules/Core/usings/constantes'
import { useDocumentosPdf } from '@/modules/Configuracion/composables/useDocumentosPdf'
import { useModal } from '@/modules/Core/composables/useModal'
import ModalDocumentos from '@/modules/Asistencial/components/ModalDocumentos.vue'
import type { IConfDocjsonparametros } from '@/modules/Configuracion/interfaces'
import type { IDocumentoFirma } from '@/modules/Configuracion/interfaces/IDocumento'
import type { IOrigenAtencion } from '@/modules/Asistencial/interfaces/IOrigenAtencion'

const router = useRouter()
const { openModal } = useModal()

const editAdmi = ref(false)
const origenAtencion = ref<IOrigenAtencion | null>(null)
const { openAndGeneratePdf } = useDocumentosPdf()
const { extractTechComponents } = useAdmisionTechComponents()
const emit = defineEmits([
  'fnClose'
])

const props = defineProps({
  admision: {
    type: Object as () => ICitaAdmision,
    default: null
  },
  origenAuditoriaFinanciera: {
    type: Boolean,
    default: false
  },
  modalId: {
    type: String,
    default: 'view_admision'
  }
})

const tipoImpresionComputed = computed(() => {
  if (admisionData.value?.ciadTipoAdmision === "VACUNA") {
    return tipoImpresionAsistencialDefault.VACUNA
  } else if (admisionData.value?.ciadTipoAdmision === "PROCEDIMIENTO") {
    return tipoImpresionAsistencialDefault.PROCEDIMIENTOS
  } else if (admisionData.value?.ciadTipoAdmision === "LABORATORIO") {
    return tipoImpresionAsistencialDefault.LABORATORIOS
  } else if (admisionData.value?.ciadTipoAdmision === "OTROS SERVICIOS") {
    return tipoImpresionAsistencialDefault['OTROS SERVICIOS']
  } else {
    return tipoImpresionAsistencialDefault.HISTORIA
  }
})

const editarAdmision = async () => {
  editAdmi.value = true
  try {
    if (!admisionData.value?.ciadId) return
    window.notificacion?.notificacion(
      'info',
      `Verificando si la admisión tiene cuenta de cobro asociada...`
    )
    const { data: invoiceItems } = await admisionService.getInvoicesitems(admisionData.value.ciadId)
    if (invoiceItems.body.count === 0) {
      router.push({
        name: 'AdmisionEdit',
        params: { ciadId: admisionData.value.ciadId.toString() },
        query: props.origenAuditoriaFinanciera ? { origen: 'auditoria-financiera' } : undefined
      })
      closeModal(props.modalId)
      emit('fnClose')
    } else {
      window.notificacion?.notificacion(
        'failure',
        `No se puede editar la admisión porque ya esta asociada a una cuenta de cobro.`
      )
    }
  } finally {
    editAdmi.value = false
  }
}

const admisionData = ref<ICitaAdmision | null>()
const trazabilidad = ref<IAudiEvento[]>([])
const loading = ref(true)
const skeletonAdmision = {
  filas: [
    {
      cols: [
        {
          style: 'width:25%',
          lines: [{ style: 'width:100%', class: 'h-[400px]' }]
        },
        {
          style: 'width:75%;padding-left:5px',
          lines: [{ style: 'width:100%', class: 'h-[400px]' }]
        }
      ]
    }
  ]
}

const eventoLabel: Record<string, { label: string }> = {
  'agendar':          { label: 'Agendado por' },
  'reagendar':        { label: 'Reagendado por' },
  'admisionar':       { label: 'Admisionado por' },
  'editar-admision':  { label: 'Admisión editada por' },
  'atender':          { label: 'Atendido por' },
  'abrir-historia':   { label: 'Historia abierta por' },
  'reabrir-historia': { label: 'Historia reabierta por' },
}

const firstTecnology = computed(() => {
  return admisionData.value?.technologias?.[0] || null
})

const esOrdenExterna = computed(() => origenAtencion.value?.tipo === 'EXTERNA')

const puedeFirmarConsentimiento = computed(() => {
  const estado = admisionData.value?.ciadTipoEstado
  return !!admisionData.value?.ciadId && (estado === 'ADMISIONADO' || estado === 'FINALIZADO')
})

const abrirConsentimiento = async () => {
  const admision = admisionData.value
  if (!admision) return
  const parametros = [
    {
      cdjpNombre: 'P_CIAD_ID',
      valor: admision.ciadId?.toString()
    }
  ] as IConfDocjsonparametros[]
  const setDatosPaciente = (firma: IDocumentoFirma) => {
    const paciente = admision.paciente
    firma.nombre = (paciente?.copeFullName as string) || ''
    firma.identificacion = `${paciente?.tipoidentificacion?.comaNombrecorto ?? ''} ${paciente?.copeIdentificacion ?? ''}`.trim()
  }
  await openModal('offset-consentimientos', {
    onlyOne: false,
    component: ModalDocumentos,
    props: {
      parametros,
      setDatosPaciente,
      origen: 'ADMISION',
      ciadId: admision.ciadId,
      sinServicio: true,
      ciadTipoEstado: admision.ciadTipoEstado
    }
  })
}

const actualizarOrigenAtencion = (origen: IOrigenAtencion | null) => {
  origenAtencion.value = origen
}
// Sin cita previa (ciagId null) siempre hubo admisión; con cita, ciadTipoRegimen
// solo lo llena el form de admisión
const huboAdmision = computed(() =>
  !admisionData.value?.ciagId || admisionData.value?.ciadTipoRegimen != null
)

const detalleInasistencia = computed(() => {
  if (admisionData.value?.ciadTipoEstado !== 'INASISTIDO') return null
  const tech = admisionData.value?.technologias?.find(t => t.cittEstado === 'INASISTIDO')
  const motivo = tech?.motivoinasistencia?.comaNombrelargo || tech?.motivoinasistencia?.comaNombrecorto
  const descripcion = tech?.cittMotivoInasistencia
  if (!motivo && !descripcion) return null
  return { motivo: motivo || 'Sin motivo registrado', descripcion }
})

const datosAdmision = computed(() => {
  if (!admisionData.value) return []
  return [
    {
      name: admisionData.value.contrato?.cocoNombre || 'Sin contrato',
      tipo: 'contrato',
      fullWidth: true
    },
    {
      name: admisionData.value.contrato?.empresa?.coemNombre || 'Sin empresa',
      tipo: 'empresa',
      fullWidth: true
    },
    {
      name: admisionData.value.ciadTipoAdmision || 'Sin tipo',
      tipo: 'tipoAtencion'
    },
    {
      name: admisionData.value.ciadTipoEstado || 'Sin estado',
      tipo: 'estadoAsistencial'
    }
  ]
})

const formatCambio = (c: { area: string; operacion: string; ref: string; anterior?: string | null; nuevo?: string | null }) => {
  const base = `[${c.area}] ${c.ref}`
  if (c.operacion === 'modificado' && c.anterior != null && c.nuevo != null) return `${base}: ${c.anterior} → ${c.nuevo}`
  if (c.operacion === 'agregado') return c.nuevo ? `${base} agregado: ${c.nuevo}` : `${base} agregado`
  if (c.operacion === 'eliminado') return c.anterior ? `${base} eliminado: ${c.anterior}` : `${base} eliminado`
  return base
}

const ROUTES_TECH = {
  'OTROS SERVICIOS': '/asistenciales/OtrosServicios',
  'PROCEDIMIENTO': '/asistenciales/Procedimiento',
  'LABORATORIO': '/asistenciales/Laboratorio',
  'CONSULTA': '/asistenciales/Consulta',
  'VACUNA': '/asistenciales/Vacuna',
} as Record<string, string>
const pushToRouteByStateTech = (rute: string, tipo: string, cittId: number, ciadId?: number) => {
  closeModal('view_admision')
  const id = (tipo === 'LABORATORIO') ? ciadId : cittId
  router.push(`${ROUTES_TECH[tipo]}/${rute}/` + id)
}
onMounted(async () => {
  const { data } = await admisionService.view(props.admision.ciadId as number, { withAssociationDoc: true, withAssociationDiag: true, withPacienteSnapshot: true })
  admisionData.value = data.body
  if (admisionData.value?.ciadTipoAdmision === 'LABORATORIO') {
    await extractTechComponents(admisionData.value, ['TRAMITADO'])
  }
  loading.value = false
  const cittId = admisionData.value?.technologias?.[0]?.cittId
  if (cittId) {
    const { data: td } = await trazabilidadService.search({ cittId, limit: 50 })
    trazabilidad.value = td.body?.rows ?? []
  }
})

const hasMultipleTramitadosLabs = computed(() => {
  if (admisionData.value?.ciadTipoAdmision !== 'LABORATORIO') return false
  const tramitados = admisionData.value?.technologias?.filter(t => t.cittEstado === 'TRAMITADO') || []
  return tramitados.length >= 2
})
</script>
